// TODO: uncomment when functionality to upload plots in UI is fixed
import {
  Container,
  Divider,
  // DropdownItemLink,
  // DropdownItemLinkProps,
  // RiEditLine,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  BenchmarkType,
  CLIMATE_RISK_FACT_TYPES,
  ClimateRiskRatingOrder,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
  R1FactType,
  Scenario,
} from '@/api/rest/resources/types/fact';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { PlotType } from '@/api/rest/resources/types/plot';
import { DefaultWidgetNames, GraphTileType, WidgetType } from '@/api/rest/resources/types/widget';
import { SecondaryNav } from '@/components';
import { GraphFact } from '@/components/Charts/types';
import { IndicatorProvider, useHasIndicatorData } from '@/components/IndicatorCard/IndicatorContextProvider';
import {
  MapRadiusContextProvider,
  NO_RADIUS,
  useMapRadiusContext,
} from '@/components/MapOverview/components/MapRadiusContextProvider';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { BiodiversityExplainer } from '@/components/NCExplainers/BiodiversityExplainer';
import { CanopyCoverExplainer } from '@/components/NCExplainers/CanopyCoverExplainer';
import { CanopyHeightExplainer } from '@/components/NCExplainers/CanopyHeightExplainer';
import { LandCoverChangeExplainer } from '@/components/NCExplainers/LandCoverChangeExplainer';
import { ProtectedAreasExplainer } from '@/components/NCExplainers/ProtectedAreasExplainer';
import { WaterExplainer } from '@/components/NCExplainers/WaterExplainer';
import { BetaPill } from '@/components/Pill/BetaPill';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { LAYER_OPTIONS } from '@/components/widgets/o/Map/constants';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import {
  COMPLIANCE_LOGOS_BIODIVERSITY,
  COMPLIANCE_LOGOS_CANOPY_COVER,
  COMPLIANCE_LOGOS_CANOPY_HEIGHT,
  COMPLIANCE_LOGOS_LAND_COVER_CHANGE,
  COMPLIANCE_LOGOS_PROTECTED_AREAS,
  COMPLIANCE_LOGOS_WATER,
} from '@/config/constants';
import { useBenchmarkSelection } from '@/hooks/useBenchmarkSelection';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useIsLandscapeViewIndicatorProductPurchased } from '@/hooks/useLandscapeView';
import { useMembershipType } from '@/hooks/useMembershipType';
import { usePathSet } from '@/hooks/usePathSet';
import { useWidgetSlotConfigurationByNameType } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { getBenchmarksFromFacts, useBenchmarksForPlot } from '@/pages/shared/hooks/useBenchmarkForFact';
import { useMenuItems } from '@/pages/shared/hooks/useMenuItems';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { usePlot } from '@/pages/shared/hooks/usePlot';
import { usePlotFacts, usePlotFactsByRadius } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray, getFactsFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { AboveGroundCarbon } from './components/aboveGroundCarbon/AboveGroundCarbonCard';
import { BiodiversityGraphTile } from './components/biodiversity/GraphTile';
import { BiodiversityStatsTile } from './components/biodiversity/StatsTile';
import { CanopyCoverGraphTile } from './components/canopyCover/GraphTile';
import { CanopyCoverCardStatsTile } from './components/canopyCover/StatsTile';
import { CanopyHeightGraphTile } from './components/canopyHeight/GraphTile';
import { CanopyHeightCardStatsTile } from './components/canopyHeight/StatsTile';
import { BelowGroundCarbon } from './components/carbon/BelowGroundCarbonCard';
import { HabitatIntactness } from './components/habitatIntactness/HabitatIntactnessCard';
import { HabitatIntactnessThreatsBreakdown } from './components/habitatThreatsBreakdown/HabitatIntactnessThreatsBreakdownCard';
import { HabitatIntactnessThreatsTable } from './components/habitatThreatsTable/HabitatIntactnessThreatsTableCard';
import { LandCoverChangeGraphTile } from './components/landCover/GraphTile';
import { LandCoverChangeStatsTile } from './components/landCover/StatsTile';
import { NDVI } from './components/ndvi/NDVICard';
import { PlotActionsDropdown } from './components/PlotActionsDropdown';
import { PlotCardWrapper } from './components/PlotCardWrapper';
import { PlotMap } from './components/PlotMap';
import { PlotOverview } from './components/PlotOverview';
import { ProtectedAreasGraphTile } from './components/protectedAreas/GraphTile';
import { ProtectedAreasStatsTile } from './components/protectedAreas/StatsTile';
import { ThreatenedSpecies } from './components/threatenedSpecies/ThreatenedSpeciesCard';
import { PrecipitationStatsTile } from './components/water/HistoricalStatsTile';
import { SMPrecipitationGraphTile } from './components/water/SMPrecipitationGraphTile';
import { WaterHoldingCapacity } from './components/water/WaterCard';
import { LandscapeViewContextProvider, useLandscapeViewContext } from './LandscapeViewContextProvider';

export const DefaultPlotView = () => {
  const { t } = useTranslation();
  const pathSet = usePathSet();

  const plot = usePlot().data;
  const widgetSlotsConfiguration = useWidgetSlotConfigurationByNameType(
    DefaultWidgetNames.default_template_plot_detail,
    WidgetType.t_page,
  );

  const plotType = plot.type;
  // TODO: MVP-4728 - hardcode this to true to switch to forest indicators
  // TODO: MVP-4724 - swap out with PlotType.FORESTRY
  const isForestPlotType = plotType === PlotType.TREE_COVER;

  const location = useLocation();

  const projectId = (plot.project ?? plot.project_id) as string;

  const menuItems = useMenuItems();

  return (
    <MapRadiusContextProvider>
      <LandscapeViewContextProvider>
        <SignedInPageLayout items={menuItems} withCustomContentContainer>
          <SecondaryNav
            title={plot.name}
            backPath={buildPath(pathSet.projectPlots, {
              pathParams: { projectId },
              queryParams: { page: location.state?.plotPage },
            })}
            action={<PlotActions />}
            className='justify-start !gap-12'
          />

          <Container gutterWidth={{ sm: 4 }} className='gap-10' data-testid='default-plot-view'>
            <div className='h-[60vh] animate-in fade-in'>
              <PlotMap plot={plot} layers={LAYER_OPTIONS} />
            </div>
            <PlotOverview />
            <ClimateRisk />
            <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />
            <Stack spacing={8} className='mt-10'>
              <span className='typography-overline'>{t('shared.projects.plot.landMonitoring')}</span>
              <ProtectedAreas />
              <ThreatenedSpecies />
              <LandCoverChange />
              <AboveGroundCarbon />
              <BelowGroundCarbon />
              <WaterHoldingCapacity />
              <Precipitation />
              {/* {isForestPlotType && (
                  <>
                    <CanopyHeight />
                    <CanopyCover />
                  </>
                )} */}
              {!isForestPlotType && <Biodiversity />}
              <NDVI />
              <HabitatIntactness />
              <HabitatIntactnessThreatsBreakdown />
              <HabitatIntactnessThreatsTable />
              {/* TODO: MVP-8557 unhide deforestation cards for agriculture */}
              {/* <Deforestation /> */}
            </Stack>
          </Container>
        </SignedInPageLayout>
      </LandscapeViewContextProvider>
    </MapRadiusContextProvider>
  );
};

const ClimateRisk = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('global.analysis.carbonStorageAg')}>
      <ClimateRiskCard />
    </PlotCardWrapper>
  );
};

const ClimateRiskCard: FC = () => {
  const facts = usePlotFacts(Object.keys(CLIMATE_RISK_FACT_TYPES)) ?? [];

  const risks = facts
    .map((fact) => {
      return getFactFromFactArray(facts, fact.name, {
        [PlotReportFactLabelNameEnum.scenario]: Scenario.baseline,
      });
    })
    .filter((risk) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return !isNullish(risk?.value) && typeof (risk?.value as Record<string, any>).value === 'number';
    })
    .sort((a, b) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const aRating = (a?.value as Record<string, any>).rating;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bRating = (b?.value as Record<string, any>).rating;
      return ClimateRiskRatingOrder[aRating as keyof typeof ClimateRiskRatingOrder] >
        ClimateRiskRatingOrder[bRating as keyof typeof ClimateRiskRatingOrder]
        ? -1
        : 1;
    })
    .map((risk) => risk?.name);

  const riskOverviewConfig = {
    name: 'climate_risk_overview',
    type: WidgetType.o_c_risk_overview,
    visible: facts.length > 0,
    relatedData: {
      className: 'flex flex-row flex-wrap gap-4',
      risks,
    },
  };

  return <WidgetSlotRenderer nestedWidget={riskOverviewConfig} />;
};

const Precipitation = () => {
  const { t } = useTranslation();

  const [showCard, setShowCard] = useState(true);

  if (!showCard) {
    return null;
  }

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.water.labels.precipitation')}>
      <IndicatorProvider>
        <PrecipitationPlotCard setShowCard={setShowCard} />
      </IndicatorProvider>
    </PlotCardWrapper>
  );
};

const PrecipitationPlotCard = ({ setShowCard }: { setShowCard: (show: boolean) => void }) => {
  const { t } = useTranslation();

  const factSet = {
    graph: R1FactType.r1_historic_water_and_soil_moisture_graph,
    latestAnalysis: R1FactType.r1_soil_moisture_latest_analysis_total,
    smTrend: R1FactType.r1_soil_moisture_trend_change,
    precipitationTrend: R1FactType.r1_precipitation_trend_change,
  };

  const facts = usePlotFacts(Object.values(factSet));

  const graphData = getFactFromFactArray<GraphFact[]>(facts, factSet.graph)?.value;

  const latestAnalysisFact = getFactFromFactArray<number>(facts, factSet.latestAnalysis);
  const smTrendFact = getFactFromFactArray<number>(facts, factSet.smTrend);
  const precipitationTrendFact = getFactFromFactArray<number>(facts, factSet.precipitationTrend);

  const hasChartData = !isNullish(graphData);
  const hasStatsData =
    !isNullish(latestAnalysisFact?.value) ||
    !isNullish(smTrendFact?.value) ||
    !isNullish(precipitationTrendFact?.value);

  useEffect(() => {
    if (!hasChartData && !hasStatsData) {
      setShowCard(false);
    }
  }, [hasChartData, hasStatsData, setShowCard]);

  return (
    <NCCard explainer={<WaterExplainer />} data-testid='precipitation-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.water.labels.precipitation')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <SMPrecipitationGraphTile graphData={graphData} />
        <PrecipitationStatsTile
          latestAnalysisFact={latestAnalysisFact}
          smTrendFact={smTrendFact}
          precipitationTrendFact={precipitationTrendFact}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_WATER} />
    </NCCard>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
const CanopyHeight = () => {
  const { t } = useTranslation();
  // TODO: If bringing this live, refactor to prevent components from using "usePlotFact". Use the same approach as in the other cards

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.forest.labels.canopyHeight')}>
      <IndicatorProvider>
        <CanopyHeightPlotCard />
      </IndicatorProvider>
    </PlotCardWrapper>
  );
};

const CanopyHeightPlotCard = () => {
  const { t } = useTranslation();

  const hasIndicatorData = useHasIndicatorData();

  if (!hasIndicatorData) {
    return null;
  }

  return (
    <NCCard explainer={<CanopyHeightExplainer />}>
      <NCCardHeader title={t('shared.ncaDetail.details.forest.labels.canopyHeight')} />
      <NCCard2SectionLayout>
        <CanopyHeightGraphTile />
        <CanopyHeightCardStatsTile />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_CANOPY_HEIGHT} />
    </NCCard>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
const CanopyCover = () => {
  const { t } = useTranslation();

  // TODO: If bringing this live, refactor to prevent components from using "usePlotFact". Use the same approach as in the other cards

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.forest.labels.canopyCover')}>
      <IndicatorProvider>
        <CanopyCoverPlotCard />
      </IndicatorProvider>
    </PlotCardWrapper>
  );
};

const CanopyCoverPlotCard = () => {
  const { t } = useTranslation();

  const hasIndicatorData = useHasIndicatorData();

  if (!hasIndicatorData) {
    return null;
  }

  return (
    <NCCard explainer={<CanopyCoverExplainer />}>
      <NCCardHeader title={t('shared.ncaDetail.details.forest.labels.canopyCover')} />
      <NCCard2SectionLayout>
        <CanopyCoverGraphTile />
        <CanopyCoverCardStatsTile />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_CANOPY_COVER} />
    </NCCard>
  );
};

const ProtectedAreas = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.protectedAreas.labels.protectedAreas')}>
      <ProtectedAreasCard />
    </PlotCardWrapper>
  );
};

const ProtectedAreasCard = () => {
  const { isLandscapeActive } = useLandscapeViewContext();
  const { t } = useTranslation();

  const factNames = {
    graph: R1FactType.protected_areas_area,
    latestAnalysis: R1FactType.protected_areas_area,
    minDistance: R1FactType.protected_areas_min_distance,
  };

  const facts = usePlotFactsByRadius(Object.values(factNames));

  const graphData = getFactsFromFactArray(facts, factNames.graph) ?? [];
  const statsData = [
    ...(getFactsFromFactArray(facts, factNames.latestAnalysis) ?? []),
    ...(getFactsFromFactArray(facts, factNames.minDistance) ?? []),
  ];

  if (!isLandscapeActive) {
    return null;
  }

  if (graphData.length === 0 && statsData.length === 0) {
    return null;
  }

  return (
    <NCCard explainer={<ProtectedAreasExplainer />}>
      <NCCardHeader
        title={t('shared.ncaDetail.details.protectedAreas.labels.protectedAreas')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <ProtectedAreasGraphTile protectedAreas={graphData} />
        <ProtectedAreasStatsTile protectedAreasSummary={statsData} />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_PROTECTED_AREAS} />
    </NCCard>
  );
};

const Biodiversity = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('global.analysis.protectedOnFarmHabitat')}>
      <IndicatorProvider>
        <BiodiversityPlotCard />
      </IndicatorProvider>
    </PlotCardWrapper>
  );
};

const BiodiversityPlotCard = () => {
  const { t } = useTranslation();

  const factNames = {
    graph: R1FactType.r1_biodiversity_zone_percent_historic_graph,
    latestAnalysis: R1FactType.r1_biodiversity_latest_analysis,
    oneYearUplift: R1FactType.r1_biodiversity_buffer_zone_pct_previous_1y_uplift_change,
    benchmark: R1FactType.biodiversity_buffer_zone_pct_benchmark_graph,
  };

  const facts = usePlotFacts(Object.values(factNames));

  const graphData = getFactFromFactArray<GraphFact[]>(facts, factNames.graph)?.value;
  const latestAnalysisFact = getFactFromFactArray(facts, factNames.latestAnalysis);
  const oneYearUpliftFact = getFactFromFactArray(facts, factNames.oneYearUplift);

  const highBenchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  });
  const moderateBenchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
  });
  const lowBenchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
  });

  const graphBenchmarks = getBenchmarksFromFacts({
    highBenchmarkFact,
    moderateBenchmarkFact,
    lowBenchmarkFact,
  });

  const statsBenchmarks = useBenchmarksForPlot(factNames.benchmark);

  const organizationId = useOrganizationId();
  const indicator = GraphTileType.protected_on_farm_habitat;
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useBenchmarkSelection(indicator);

  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);

  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmarkId);

  const hasChartData = !isNullish(graphData);
  const hasStatsData = !isNullish(latestAnalysisFact?.value) || !isNullish(oneYearUpliftFact?.value);

  if (!hasChartData && !hasStatsData) {
    return null;
  }

  return (
    <NCCard explainer={<BiodiversityExplainer />} data-testid='biodiversity-nca'>
      <NCCardHeader
        title={
          <span className='flex flex-row items-center gap-3'>
            {t('global.analysis.protectedOnFarmHabitat')}
            <BetaPill />
          </span>
        }
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <BiodiversityGraphTile
          data={graphData}
          benchmarks={graphBenchmarks}
          highBenchmark={highBenchmarkFact}
          moderateBenchmark={moderateBenchmarkFact}
          lowBenchmark={lowBenchmarkFact}
          activeCustomBenchmark={activeCustomBenchmark}
          selectedBenchmarkId={selectedBenchmarkId}
          onBenchmarkSelect={setSelectedBenchmarkId}
        />
        <BiodiversityStatsTile
          latestAnalysisFact={latestAnalysisFact}
          oneYearUpliftFact={oneYearUpliftFact}
          benchmarks={statsBenchmarks}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_BIODIVERSITY} />
    </NCCard>
  );
};

const LandCoverChange = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.landCoverChange.labels.landCoverChange')}>
      <LandCoverChangeCard />
    </PlotCardWrapper>
  );
};

const LandCoverChangeCard = () => {
  const { isLandscapeActive } = useLandscapeViewContext();
  const { t } = useTranslation();

  const factName = R1FactType.landcover_by_landscape_type_historic_pct_graph;

  const facts = usePlotFactsByRadius([factName]);

  const cardData = (getFactsFromFactArray(facts, factName) ?? []) as PlotReportFactElement<GraphFact[]>[];

  // TODO: MVP-6869 clean up this, FE should not be merging all the facts into one
  const cardFact = cardData?.reduce(
    (acc: PlotReportFactElement<GraphFact[]> | null, fact: PlotReportFactElement<GraphFact[]>) => {
      if (fact.name !== R1FactType.landcover_by_landscape_type_historic_pct_graph) {
        return acc;
      }

      if (!acc) {
        return fact;
      }

      return {
        ...acc,
        value: [...(acc.value ?? []), ...(fact.value ?? [])],
      };
    },
    null,
  );

  if (!isLandscapeActive || !cardFact || !cardFact?.value) {
    return null;
  }

  return (
    <NCCard explainer={<LandCoverChangeExplainer />}>
      <NCCardHeader
        title={t('shared.ncaDetail.details.landCoverChange.labels.landCoverChange')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <LandCoverChangeGraphTile landCoverChange={cardFact} />
        <LandCoverChangeStatsTile landCoverChange={cardFact} />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_LAND_COVER_CHANGE} />
    </NCCard>
  );
};

const PlotActions = () => {
  const membershipType = useMembershipType();

  const isLandSteward = membershipType === MembershipWithOrganizationTypeEnum.land_steward;

  if (!isLandSteward) {
    return <LandscapeViewToggle />;
  }

  return (
    <Stack direction='row' className='grow justify-between' centerMain>
      <LandscapeViewToggle />
      <PlotActionsDropdown />
    </Stack>
  );
};

const LandscapeViewToggle = () => {
  const { t } = useTranslation();
  const { isLandscapeActive, setIsLandscapeActive } = useLandscapeViewContext();

  const { setRadius } = useMapRadiusContext();
  const isDisabled = !useIsLandscapeViewIndicatorProductPurchased();

  const TOGGLE_VALUES = {
    ON: 'on',
    OFF: 'off',
  } as const;

  const handleValueChange = (newValue: (typeof TOGGLE_VALUES)[keyof typeof TOGGLE_VALUES]) => {
    if (isDisabled) return;

    setIsLandscapeActive(newValue === TOGGLE_VALUES.ON);

    if (newValue === TOGGLE_VALUES.OFF) {
      setRadius(NO_RADIUS);
    }
  };

  const disabledButtonClasses = 'cursor-not-allowed opacity-50';

  return (
    <Stack direction='row' spacing={4} centerMain data-testid='landscape-view-toggle'>
      <span className={isDisabled ? 'typography-body1 opacity-60' : 'typography-body1'}>
        {t('shared.projects.plot.landscapeView.toggle.title')}
        {isDisabled && (
          <Tooltip>
            <TooltipTrigger className='items-center ml-1'>
              <Icon icon='ri-information-line' />
            </TooltipTrigger>
            <TooltipContent>
              <Stack>
                <span className='typography-body2'>{t('shared.projects.plot.landscapeView.toggle.tooltip')}</span>
              </Stack>
            </TooltipContent>
          </Tooltip>
        )}
      </span>

      <ToggleButtonGroup
        size='small'
        value={isLandscapeActive ? TOGGLE_VALUES.ON : TOGGLE_VALUES.OFF}
        onChange={handleValueChange}
      >
        <ToggleButton
          value={TOGGLE_VALUES.ON}
          disabled={isDisabled}
          className={isDisabled ? disabledButtonClasses : ''}
          data-testid='landscape-view-toggle-on'
        >
          {t('shared.projects.plot.landscapeView.toggle.on')}
        </ToggleButton>
        <ToggleButton
          value={TOGGLE_VALUES.OFF}
          disabled={isDisabled}
          className={isDisabled ? disabledButtonClasses : ''}
          data-testid='landscape-view-toggle-off'
        >
          {t('shared.projects.plot.landscapeView.toggle.off')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
};

/* type MenuDropdownItemLinkProps = DropdownItemLinkProps & {
 *   disabledHint: string;
 * };
 *  */
/* const MenuDropdownItemLink: FC<MenuDropdownItemLinkProps> = ({ children, disabledHint, to, ...delegated }) => {
 *   if (!delegated.disabled) {
 *     return (
 *       <DropdownItemLink to={to} {...delegated}>
 *         {children}
 *       </DropdownItemLink>
 *     );
 *   }
 *
 *   return (
 *     <Tooltip>
 *       <TooltipTrigger asChild>
 *         <DropdownItem {...delegated}>{children}</DropdownItem>
 *       </TooltipTrigger>
 *       <TooltipContent sideOffset={5} side='left'>
 *         {disabledHint}
 *         <TooltipArrow />
 *       </TooltipContent>
 *     </Tooltip>
 *   );
 * }; */
